import React, { useCallback, useContext, useMemo } from 'react'
import { CandlestickData, SingleValueData } from 'lightweight-charts'
import { AppContext } from '../../../App'
import { Security } from '../../../proto/generated/security'
import { ExtraSmallButton } from '../../custom'
import AddAlertIcon from '../../../assets/icons/AddAlertIcon'
import { ShortcutsCategories, ShortcutType } from '../../KeyboardShortcuts/Shortcuts.types'
import useShortcuts from '../../../hooks/useShortcuts'
import { AlertDialogShortcutsKeyName } from '../../KeyboardShortcuts/ShortcutsKeymap'
import { UserAlert } from '../../../proto/generated/nexus'
import { useDispatch } from 'react-redux'
import { setAlertModalData } from '../../../reducer/alerts.reducer'
import { createUserAlertFromSnapshot } from '../../../util/alerts'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { Tooltip, Box } from '@mui/material'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer'
import { useUser } from '../../../context/UserContext'
import { FeatureCategory, FeatureSubCategory } from '../../adminUserRestrictions/Restrictions.constants'
import { selectUserSubscriptionPlansData } from '../../../selectors/user'
import { useSelector } from 'react-redux'
import { FeatureCode } from '../../../util/RestrictionFeatureCode'

export interface AlertData {
    grps: AlertSeriesGrp[];
    alert?: UserAlert,
    securities: Security[]
}

export interface AlertSeriesGrp {
    id: string;
    name: string;
    code: string;
    description?: string;
    serieses: AlertSeries[];
    primary: boolean;
}

export interface AlertSeries {
    id: string;
    name: string;
    data: CandlestickData | SingleValueData;
}

interface AlertMenuItemProps {
    viewKey: string,
    disabled?: boolean,
    customStyle?: any
    customPriceValue?: string
    handleCloseMenu?: Function
}

function AlertMenuItem(props: AlertMenuItemProps) {
    const dispatch = useDispatch()
    const { disabled = false, viewKey } = props
    const appContext = useContext(AppContext);
    const { user: { hasAccess } } = useUser()
    const subscriptionPlans = useSelector(selectUserSubscriptionPlansData)

    const handleButtonClick = useCallback(() => {
        if(!hasAccess(FeatureCategory.CHART, FeatureSubCategory.ALERTS)) {
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Alerts",
                featureRestrictionCode:FeatureCode.CHART_ALERTS,
                group: Group.CHART
            }))
            return
        }
        if (disabled) return; // Check manually for shortcut trigger;
        const chartSnapshotMethod = appContext.getChartSnapshot[viewKey]
        const snapshot = chartSnapshotMethod ? chartSnapshotMethod() : null;

        if (!snapshot) return
        dispatch(setAlertModalData({ chartSnapshot: snapshot, alert: createUserAlertFromSnapshot(snapshot), edit: false }))
    }, [appContext.getChartSnapshot, disabled, viewKey, dispatch, hasAccess])

    const shortcuts: ShortcutType = useMemo(() => ({
        [AlertDialogShortcutsKeyName.openAlertDialog]: () => {
            AnalyticsManager.logEvent('Create Alert Dialog Opened', { source: 'keyboard' })
            handleButtonClick()
        }
    }), [handleButtonClick])

    useShortcuts(ShortcutsCategories.ALERT_DIALOG, shortcuts)

    return (
        <div>
            <Tooltip
                 title={
                    <span style={{fontSize:'12px'}}>
                        Create Alert : <Box
                        component="kbd"
                        sx={{
                            display: 'inline-block',
                            padding: '2px 6px',
                            fontSize: '12px',
                            backgroundColor: 'grey',
                            borderRadius: '4px',
                            margin: '0 2px',
                        }}
                    >
                       ⌥
                    </Box>+<Box
                        component="kbd"
                        sx={{
                            display: 'inline-block',
                            padding: '2px 6px',
                            fontSize: '12px',
                            backgroundColor: 'grey',
                            borderRadius: '4px',
                            margin: '0 2px',
                            
                        }}
                    >
                       A
                    </Box>
                    </span>
                }
                arrow
                placement="bottom"
            >
                <span>
                <ExtraSmallButton
                    id={'add-alert-menu-button'}
                    size="small"
                    color="inherit"
                    onClick={handleButtonClick}
                    disabled={disabled}
                    sx={{ width: '100%' }}
                    data-event={'StockBar Alert Menu Opened'}
                >
                    <AddAlertIcon />
                </ExtraSmallButton>
                </span>
            </Tooltip>
        </div>
    )
}

export default React.memo(AlertMenuItem)